/**
 * Synthetic trade generator for realistic market activity.
 * Generates trades based on VLP quote data to match order book activity.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type { MarketTrade, Side } from "./market-trades";
import type { VlpQuote } from "@/backend/types";
import { parseDecimal } from "@/backend/types";

type UseSyntheticTradesOptions = {
  /** VLP quote data for the market */
  quote: VlpQuote | null;
  /** Maximum number of trades to display */
  maxTrades?: number;
  /** Average interval between trades in ms (will vary randomly) */
  avgTradeInterval?: number;
  /** Base precision for amounts */
  basePrecision?: number;
  /** Quote precision for prices */
  quotePrecision?: number;
};

/**
 * Hook that generates realistic synthetic trades based on VLP data.
 * Creates an active-looking trade feed with varied amounts and timing.
 */
export const useSyntheticTrades = ({
  quote,
  maxTrades = 50,
  avgTradeInterval = 800,
  basePrecision = 8,
  quotePrecision = 2,
}: UseSyntheticTradesOptions) => {
  const [trades, setTrades] = useState<MarketTrade[]>([]);
  const tradeIdRef = useRef(Date.now());
  const lastTradeTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parse quote data
  const parsedQuote = useMemo(() => {
    if (!quote) return null;
    return {
      bidPrice: parseDecimal(quote.bid_price),
      askPrice: parseDecimal(quote.ask_price),
      oraclePrice: parseDecimal(quote.oracle_price),
    };
  }, [quote]);

  // Generate a single realistic trade
  const generateTrade = useCallback((): MarketTrade | null => {
    if (!parsedQuote) return null;

    const { bidPrice, askPrice, oraclePrice } = parsedQuote;
    
    // Determine trade side with slight bias based on price movement
    // More buys when price is rising, more sells when falling
    const midPrice = (bidPrice + askPrice) / 2;
    const priceMovementBias = (midPrice - oraclePrice) / oraclePrice;
    const buyProbability = 0.5 - priceMovementBias * 2; // Slight counter-trend
    const side: Side = Math.random() < buyProbability ? "buy" : "sell";

    // Trade price is near the spread
    // Buys happen at/near ask, sells at/near bid
    const basePrice = side === "buy" ? askPrice : bidPrice;
    // Add small random variance (within 0.01% of price)
    const priceVariance = basePrice * 0.0001 * (Math.random() - 0.5);
    const price = roundToDecimals(basePrice + priceVariance, quotePrecision);

    // Generate realistic trade amount
    const amount = generateTradeAmount(oraclePrice, basePrecision);

    tradeIdRef.current += 1;

    return {
      id: `syn-${tradeIdRef.current}`,
      price,
      quantity: amount,
      time: Date.now(),
      side,
    };
  }, [parsedQuote, basePrecision, quotePrecision]);

  // Schedule next trade with variable timing
  const scheduleNextTrade = useCallback(() => {
    if (!parsedQuote) return;

    // Variable interval: between 50% and 200% of average
    // With occasional bursts (10% chance of rapid trades)
    const isBurst = Math.random() < 0.1;
    const minInterval = isBurst ? 50 : avgTradeInterval * 0.3;
    const maxInterval = isBurst ? 200 : avgTradeInterval * 2.5;
    const interval = minInterval + Math.random() * (maxInterval - minInterval);

    timeoutRef.current = setTimeout(() => {
      const newTrade = generateTrade();
      if (newTrade) {
        setTrades((prev) => {
          const updated = [newTrade, ...prev];
          return updated.slice(0, maxTrades);
        });
        lastTradeTimeRef.current = Date.now();
      }
      scheduleNextTrade();
    }, interval);
  }, [parsedQuote, avgTradeInterval, maxTrades, generateTrade]);

  // Initialize trades and start generation
  useEffect(() => {
    if (!parsedQuote) {
      setTrades([]);
      return;
    }

    // Generate initial batch of trades (simulate history)
    const initialTrades: MarketTrade[] = [];
    const now = Date.now();
    
    // Generate trades with cumulative time offsets to ensure proper ordering
    let cumulativeOffset = 0;
    for (let i = 0; i < maxTrades; i++) {
      const trade = generateTrade();
      if (trade) {
        // Each trade is older than the previous by a random interval
        // This ensures strictly descending time order
        const intervalOffset = avgTradeInterval * (0.3 + Math.random() * 0.7);
        cumulativeOffset += intervalOffset;
        trade.time = now - cumulativeOffset;
        trade.id = `syn-init-${i}`;
        initialTrades.push(trade);
      }
    }

    setTrades(initialTrades);
    lastTradeTimeRef.current = now;

    // Start generating new trades
    scheduleNextTrade();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [parsedQuote?.bidPrice, parsedQuote?.askPrice, parsedQuote?.oraclePrice, maxTrades, avgTradeInterval]);

  // Restart scheduling when dependencies change
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scheduleNextTrade]);

  return {
    trades,
    isLoading: !parsedQuote,
  };
};

/**
 * Generate a realistic trade amount based on asset price.
 * Creates a mix of small, medium, and large trades to simulate real market activity.
 */
function generateTradeAmount(oraclePrice: number, precision: number): number {
  // Determine base amount range based on price (target ~$50-5000 per trade)
  const { minAmount, maxAmount } = getAmountRange(oraclePrice);
  
  // Distribution: mostly small trades, occasional large ones
  // Use exponential distribution for realistic trade size distribution
  const u = Math.random();
  
  // 70% small trades, 25% medium trades, 5% large trades
  let sizeMultiplier: number;
  if (u < 0.70) {
    // Small trades: 5-30% of max
    sizeMultiplier = 0.05 + Math.random() * 0.25;
  } else if (u < 0.95) {
    // Medium trades: 30-70% of max
    sizeMultiplier = 0.30 + Math.random() * 0.40;
  } else {
    // Large trades: 70-150% of max (occasional whale)
    sizeMultiplier = 0.70 + Math.random() * 0.80;
  }

  const amount = minAmount + (maxAmount - minAmount) * sizeMultiplier;
  
  // Add some randomness to make amounts less uniform
  const variance = 1 + (Math.random() - 0.5) * 0.3;
  
  return roundToDecimals(amount * variance, precision);
}

/**
 * Get appropriate amount range based on asset price.
 * Targets reasonable USD values per trade for different assets.
 */
function getAmountRange(price: number): { minAmount: number; maxAmount: number } {
  // Target trade values: $50 to $5,000 USD typically
  if (price >= 50000) {
    // BTC: 0.0005 to 0.05 BTC ($50-$5,000)
    return { minAmount: 0.0005, maxAmount: 0.05 };
  }
  if (price >= 2000) {
    // ETH: 0.01 to 1.5 ETH ($30-$4,500)
    return { minAmount: 0.01, maxAmount: 1.5 };
  }
  if (price >= 500) {
    // BNB: 0.05 to 5 ($30-$3,000)
    return { minAmount: 0.05, maxAmount: 5 };
  }
  if (price >= 100) {
    // SOL/LTC: 0.2 to 25 ($30-$3,750)
    return { minAmount: 0.2, maxAmount: 25 };
  }
  if (price >= 5) {
    // DOT: 5 to 500 ($35-$3,500)
    return { minAmount: 5, maxAmount: 500 };
  }
  if (price >= 0.5) {
    // XRP/MATIC: 50 to 5000 ($25-$2,500)
    return { minAmount: 50, maxAmount: 5000 };
  }
  // ADA/DOGE: 100 to 20000 ($10-$2,000)
  return { minAmount: 100, maxAmount: 20000 };
}

/**
 * Round to specified decimal places
 */
function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
